import React, { useContext, useEffect, useState } from 'react'
import { TickmillCompanyTypes } from '@/api/Clients/Types/TickmillCompanyTypes'
import { TickmillCompanyDto } from '@/api/Identity/Types/Company'
import { CountryDto } from '@/api/Identity/Types/Country'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import {
  NPaymentProviderOrderDto,
  NPaymentProviderOrderKey,
  NPaymentProviderUpdateOrderDto,
} from '@/api/NPayments/Types/NPaymentProviderOrderDto'
import { Modal } from '@/components/CustomModal'
import {
  FormBaseButtonActionWrapper,
  FormBaseNavigationHeader,
} from '@/components/Form/Form.styles'
import { buildSelectOptions } from '@/components/Form/FormField'
import { Loading } from '@/components/Loading'
import { Select } from '@/components/Select'
import { errorToast, successToast, ToastContext } from '@/components/Toast/Toast'
import { CompanyTag } from '@/components/ui/badge'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { Field, FieldLabel } from '@/components/ui/field'
import { useAppDispatch, useAppSelector } from '@/store'
import { pageableSettings } from '@/store/grid/grid.slice'
import {
  fetchCountriesAction,
  fetchTickmillCompaniesAction,
} from '@/store/identity/identity.actions'
import { fetchDeviceTypesAction } from '@/store/nPayments/nPayments.actions'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { NPaymentsApi } from '@/utils/api/nPaymentsApi'
import { useCallbackWithForceRefresh } from '@/utils/api/useCallbackWithForceRefresh'
import { useFetchOneWithFetchingUpdate } from '@/utils/api/useFetchOneWithFetchingUpdate'
import { AuthSessionContext } from '@/utils/AuthContext'
import { isEmpty } from '@/utils/is-empty'
import { hasPermission } from '@/utils/permissionsValidator'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { NameDto } from 'api/Common/Types'
import { useDebounce } from 'use-debounce'
import { useApiClient } from 'utils/api/ApiClient'
import { links } from 'utils/featureToggles'

import MoreButton from '../../../components/MoreButton/MoreButton'
import { NPaymentPSPDirectionTypes } from './PaymentProviderOptionsAndLimitsDetailsPage'
import {
  NPaymentProviderOrderingUpdateDeleteFormValues,
  PaymentProviderOrderingForm,
} from './PaymentProviderOrderingForm'

interface EditableCell extends Omit<GridCellProps, 'onChange'> {
  field: string
  onChange: (value: number) => void
  isEditing: boolean
}

const OrderCell: React.FC<EditableCell> = (props) => {
  const { dataItem, field, onChange, isEditing } = props
  const [value, setValue] = useState<number>(dataItem[field])

  useEffect(() => {
    if (!isEditing) {
      setValue(dataItem[field])
    }
  }, [dataItem, field, isEditing])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value)
    setValue(newValue)
  }

  const handleBlur = () => {
    onChange(Number(value))
  }

  const handleSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onChange(Number(value))
    }
  }

  if (isEditing) {
    return (
      <td>
        <input
          type='number'
          value={value}
          min={0}
          onChange={handleChange}
          onKeyDown={handleSubmit}
          onBlur={handleBlur}
          className='k-input k-textbox'
          style={{ width: '100%' }}
        />
      </td>
    )
  }

  return <td>{value}</td>
}

export const PaymentProvidersOrderingPage = (): JSX.Element => {
  const nPaymentsApi = useApiClient(NPaymentsApi)
  const tickmillCompanies: TickmillCompanyDto[] = useAppSelector(
    (state) => state.identity.tickmillCompanies
  )
  const [pendingUpdates, setPendingUpdates] = useState<Map<string, NPaymentProviderUpdateOrderDto>>(
    new Map()
  )
  const [localData, setLocalData] = useState<NPaymentProviderOrderDto['methods']>([])
  const [methodFilter, setMethodFilter] = useState('')
  const [debouncedMethodFilter] = useDebounce(methodFilter, 500)
  const [enabledFilter, setEnabledFilter] = useState<boolean | undefined>(undefined)
  const [isEditMode, setIsEditMode] = useState(false)

  const [editDeleteOrderForm, setEditDeleteOrderForm] = useState<
    { action: 'edit' | 'delete'; data: NPaymentProviderOrderDto['methods'][0] } | undefined
  >()
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const dispatch = useAppDispatch()
  const identityApiClient = useApiClient(IdentityApiClient)
  const countries: CountryDto[] = useAppSelector((state) => state.identity.countries)
  const deviceTypes: NameDto[] = useAppSelector((state) => state.nPayments.deviceTypes)
  const setToast = useContext(ToastContext)

  const [selectedDeviceType, setSelectedDeviceType] = useState<NameDto | null>(null)

  useEffect(() => {
    if (isEmpty(deviceTypes)) {
      dispatch(fetchDeviceTypesAction(nPaymentsApi))
    }

    if (isEmpty(tickmillCompanies)) {
      dispatch(fetchTickmillCompaniesAction(identityApiClient))
    }
    if (isEmpty(countries)) {
      dispatch(fetchCountriesAction(identityApiClient))
    }
  }, [])

  const [tickmillCompanyId, setTickmillCompanyId] = useState<TickmillCompanyTypes>(
    tickmillCompanies[0]?.id ?? TickmillCompanyTypes.Tickmill_EU_Company
  )
  const [transactionType, setTransactionType] = useState<(typeof NPaymentPSPDirectionTypes)[0]>(
    NPaymentPSPDirectionTypes[0]
  )
  const [country, setCountry] = useState<CountryDto | null>(null)

  const crumbs = [
    { title: 'Payment Providers Ordering', link: links.administrationPaymentProvidersOrdering.url },
  ]
  const [page, setPage] = useState<{ skip: number; take: number }>({ skip: 0, take: 20 })

  const { callback, forceRefresh } = useCallbackWithForceRefresh(
    () =>
      nPaymentsApi.getPaymentProvidersOrdering({
        configurationTypeId: transactionType.id.toString(),
        tickmillCompanyId: tickmillCompanyId,
        countryId: country?.id,
        deviceTypeId: selectedDeviceType?.id,
        method: debouncedMethodFilter || undefined,
        enabled: enabledFilter,
        pageSize: 100,
      }),
    [
      transactionType.id,
      tickmillCompanyId,
      country?.id,
      selectedDeviceType?.id,
      debouncedMethodFilter,
      enabledFilter,
    ]
  )

  const { data, isLoading } = useFetchOneWithFetchingUpdate(callback)

  const filteredData = localData

  const methodNames = data?.methods.map((m) => m.method.name)

  useEffect(() => {
    if (data?.methods) {
      setLocalData(data.methods)
      setPage((prev) => ({ ...prev, skip: 0 }))
    }
  }, [data])
  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage({
      skip: event.page.skip,
      take: event.page.take,
    })
  }

  const handleOrderingSubmitAction = async (v: NPaymentProviderOrderingUpdateDeleteFormValues) => {
    let countryId
    if (data?.country?.id) {
      countryId = data?.country?.id
    }
    if (editDeleteOrderForm?.action === 'edit') {
      await nPaymentsApi.updatePaymentProviderMethodOrder({ ...v, countryId })
      setToast(successToast(`Ordering for ${v.methodCountryOrderId} updated successfully.`))
    }
    if (editDeleteOrderForm?.action === 'delete') {
      await nPaymentsApi.deletePaymentProviderMethodOrder({ ...v, countryId })
      setToast(successToast(`Ordering for ${v.methodCountryOrderId} deleted successfully.`))
    }
    setEditDeleteOrderForm(undefined)
    setIsEditMode(false)
    forceRefresh()
  }

  const handleOrderUpdate = (
    dataItem: NPaymentProviderOrderDto['methods'][0],
    newOrder: number
  ) => {
    const update: NPaymentProviderUpdateOrderDto = {
      ordering: newOrder,
      methodCountryOrderId: dataItem.id,
      userReason: 'order_update',
    }

    if (data?.country?.name) {
      update.countryId = data?.country?.id
    }

    setPendingUpdates(new Map(pendingUpdates.set(dataItem.id, update)))

    setLocalData((prevData) =>
      prevData.map((item) => (item.id === dataItem.id ? { ...item, ordering: newOrder } : item))
    )
  }

  const handleSubmitUpdates = async () => {
    if (pendingUpdates.size === 0) {
      setIsEditMode(false)
    }

    try {
      const updateMethodOrderings = Array.from(pendingUpdates.values())
      if (!updateMethodOrderings.length) {
        setPendingUpdates(new Map())
        forceRefresh()
        setIsEditMode(false)
        return
      }

      await nPaymentsApi.updatePaymentProviderMethodMultiOrdering(updateMethodOrderings)
      setToast(successToast('Orders updated successfully'))
      setPendingUpdates(new Map())
      forceRefresh()
      setIsEditMode(false)
    } catch (_: unknown) {
      setToast(errorToast('Failed to update orders'))
    }
  }

  const handleCancelEditing = () => {
    setIsEditMode(false)
    setPendingUpdates(new Map())
    setLocalData(data?.methods || [])
  }

  return (
    <React.Fragment>
      {editDeleteOrderForm?.data && (
        <Modal
          width={600}
          visible={!!editDeleteOrderForm}
          onClose={() => setEditDeleteOrderForm(undefined)}
        >
          <PaymentProviderOrderingForm
            action={editDeleteOrderForm.action}
            v={editDeleteOrderForm.data}
            onSubmit={handleOrderingSubmitAction}
            onClose={() => setEditDeleteOrderForm(undefined)}
            deviceTypes={deviceTypes}
            selectedDeviceType={selectedDeviceType}
          />
        </Modal>
      )}
      <Breadcrumbs crumbs={crumbs} />
      <Loading isLoading={isLoading}>
        <div className='max-w-150 flex flex-col gap-2'>
          <Field orientation='horizontal'>
            <FieldLabel>Company</FieldLabel>
            <Select
              className='max-w-100'
              value={tickmillCompanyId}
              options={buildSelectOptions(tickmillCompanies)}
              onChange={(v) => {
                setTickmillCompanyId(Number(v))
                setMethodFilter('')
              }}
            />
          </Field>
          <Field orientation='horizontal'>
            <FieldLabel>Transaction Type</FieldLabel>
            <Select
              className='max-w-100'
              value={transactionType.id}
              options={buildSelectOptions(NPaymentPSPDirectionTypes)}
              onChange={(v) => {
                setMethodFilter('')
                setTransactionType(
                  NPaymentPSPDirectionTypes.find(
                    (x) => x.id === Number(v)
                  ) as (typeof NPaymentPSPDirectionTypes)[0]
                )
              }}
            />
          </Field>
          <Field orientation='horizontal'>
            <FieldLabel>Country</FieldLabel>
            <Select
              className='max-w-100'
              value={country?.id}
              placeholder='Select Country'
              allowClear
              showSearch
              options={buildSelectOptions(countries)}
              onChange={(v) => {
                const c = countries.find((c) => c.id === v)
                if (c) {
                  setCountry(c)
                  setMethodFilter('')
                } else {
                  setCountry(null)
                  setMethodFilter('')
                }
              }}
            />
          </Field>
          <Field orientation='horizontal'>
            <FieldLabel>Device Type</FieldLabel>
            <Select
              className='max-w-100'
              value={selectedDeviceType?.id}
              placeholder='Select Device Type'
              allowClear
              options={buildSelectOptions(deviceTypes)}
              onChange={(v) => {
                if (v) {
                  const dt = deviceTypes.find((dt) => dt.id === Number(v))
                  setSelectedDeviceType(dt || null)
                  setMethodFilter('')
                } else {
                  setSelectedDeviceType(null)
                  setMethodFilter('')
                }
              }}
            />
          </Field>
          <Field orientation='horizontal'>
            <FieldLabel>Method</FieldLabel>
            <Select
              className='max-w-100'
              value={methodFilter}
              placeholder='All'
              allowClear
              showSearch
              options={buildSelectOptions(methodNames?.map((m) => ({ id: m, name: m })))}
              onChange={(e) => setMethodFilter(e.target.value ?? null)}
            />
          </Field>
          <Field orientation='horizontal'>
            <FieldLabel>Enabled</FieldLabel>
            <Select
              className='max-w-100'
              value={enabledFilter === undefined ? '' : enabledFilter.toString()}
              placeholder='All'
              showSearch
              allowClear
              options={[
                { label: 'All', value: '' },
                { label: 'Enabled', value: 'true' },
                { label: 'Disabled', value: 'false' },
              ]}
              onChange={(v) => {
                if (v === '') {
                  setEnabledFilter(undefined)
                  setMethodFilter('')
                } else {
                  setEnabledFilter(v === 'true')
                  setMethodFilter('')
                }
              }}
            />
          </Field>
        </div>
        <FormBaseNavigationHeader style={{ justifyContent: 'flex-end' }}>
          {!isEditMode && (
            <Button onClick={() => setIsEditMode(true)} key='button' type='button'>
              Update ordering
            </Button>
          )}
          {isEditMode && (
            <FormBaseButtonActionWrapper>
              <Button type='button' variant='outline' onClick={handleCancelEditing}>
                Cancel
              </Button>
              <Button onClick={handleSubmitUpdates} key='button' type='submit'>
                Save
              </Button>
            </FormBaseButtonActionWrapper>
          )}
        </FormBaseNavigationHeader>
        <Grid
          resizable
          data={filteredData.slice(page.skip, page.skip + page.take)}
          skip={page.skip}
          take={page.take}
          total={filteredData.length}
          pageable={pageableSettings}
          onPageChange={handlePageChange}
          style={{ zIndex: 0 }}
        >
          <Column
            field={NPaymentProviderOrderKey.tickmillCompany}
            cell={() => (
              <td>
                {data?.tickmillCompany?.id && (
                  <CompanyTag id={data.tickmillCompany.id}>{data.tickmillCompany.id}</CompanyTag>
                )}
              </td>
            )}
            title='Tickmill Company'
            className='table-columns__content'
          />
          <Column
            field={NPaymentProviderOrderKey.country}
            cell={() => <td>{data?.country?.name || ''}</td>}
            title='Country'
            className='table-columns__content'
          />
          <Column
            field={NPaymentProviderOrderKey.country}
            cell={(props) => <td>{props.dataItem.country?.name || ''}</td>}
            title='Method Country'
            className='table-columns__content'
          />
          <Column
            field='deviceType'
            title='Device Type'
            className='table-columns__content'
            cell={(props) => <td>{props.dataItem.deviceType?.name || ''}</td>}
          />
          <Column
            field={NPaymentProviderOrderKey.name}
            title='Method'
            className='table-columns__content'
          />
          {country && (
            <Column
              field='enabled'
              title='Enabled'
              width={85}
              className='table-columns__content'
              cell={(props) => (
                <td align='center' style={{ textAlign: 'center' }}>
                  <input type='checkbox' checked={!!props.dataItem.enabled} disabled />
                </td>
              )}
            />
          )}
          <Column
            field={NPaymentProviderOrderKey.ordering}
            title='Order'
            width={100}
            className='table-columns__number'
            cell={(props) => (
              <OrderCell
                {...props}
                field='ordering'
                isEditing={isEditMode}
                onChange={(newValue) => handleOrderUpdate(props.dataItem, newValue)}
              />
            )}
          />
          <Column
            width={50}
            title=''
            cell={(props) => (
              <td>
                <MoreButton
                  disabled={!hasPermission(permissions, PermissionsEnum.CanEditPaymentProviders)}
                  items={[
                    {
                      name: 'Edit',
                      onClick: () =>
                        setEditDeleteOrderForm({
                          action: 'edit',
                          data: props.dataItem,
                        }),
                    },
                    {
                      name: 'Delete',
                      disabled: !props.dataItem.country && !props.dataItem.deviceType,
                      onClick: () =>
                        setEditDeleteOrderForm({
                          action: 'delete',
                          data: props.dataItem,
                        }),
                    },
                  ]}
                />
              </td>
            )}
          />
        </Grid>
      </Loading>
    </React.Fragment>
  )
}
